import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  listAll,
  ref as storageRef,
} from "firebase/storage";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";

import SideBarForRole7 from "../components/sidebar/SideBarForRole7";
import NavbarForRole7 from "../components/navbar/NavbarForRole7";
import AutoDeleteImages from "../components/AutoDeleteImages";
import EmptyState from "../components/ui/EmptyState";
import useSnack from "../components/ui/useSnack";
import { formatRelative, formatTimestamp } from "../utils/format";
import { METRICS } from "../config/metrics";
import "./files.scss";

/* Storage prefixes written by the chart exporter, paired with the metric they
   came from so the filter reads in product language. */
const CATEGORIES = [
  { value: "ecg", label: "ECG" },
  { value: "ppg", label: "PPG" },
  { value: "accelerometer", label: "Accelerometer" },
  { value: "heartRate", label: "Heart rate" },
  { value: "spo2", label: "Blood oxygen" },
  { value: "skinTemperature", label: "Skin temperature" },
  { value: "electrodermalActivity", label: "Electrodermal activity" },
];

const RETENTION = [
  { value: "1week", label: "Older than 1 week", ms: 7 * 24 * 60 * 60 * 1000 },
  { value: "1month", label: "Older than 1 month", ms: 30 * 24 * 60 * 60 * 1000 },
  { value: "3months", label: "Older than 3 months", ms: 90 * 24 * 60 * 60 * 1000 },
  { value: "6months", label: "Older than 6 months", ms: 180 * 24 * 60 * 60 * 1000 },
];

/** Exported filenames end in an ISO timestamp: "ecg-2026-08-12T09:12:04.001Z.png" */
const timestampFromName = (name) => {
  try {
    const stem = name.substring(0, name.lastIndexOf("."));
    const tail = stem.substring(stem.indexOf("-") + 1);
    const parsed = Date.parse(tail);
    if (!Number.isNaN(parsed)) return parsed;
    const fallback = Date.parse(stem);
    return Number.isNaN(fallback) ? 0 : fallback;
  } catch {
    return 0;
  }
};

const FilesPage = () => {
  const { watchId } = useParams();
  const { snack, showSnack } = useSnack();

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("ecg");
  const [retention, setRetention] = useState("1month");

  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [confirmSweep, setConfirmSweep] = useState(false);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listAll(storageRef(getStorage(), `charts/${watchId}`));
      const items = await Promise.all(
        result.items
          .filter((item) => item.name.includes(category))
          .map(async (item) => ({
            url: await getDownloadURL(item),
            name: item.name,
            timeCreated: timestampFromName(item.name),
          }))
      );
      setImages(items.sort((a, b) => b.timeCreated - a.timeCreated));
    } catch (error) {
      console.error("Error fetching images:", error);
      showSnack("Could not load saved charts.", "error");
    } finally {
      setLoading(false);
    }
  }, [watchId, category, showSnack]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const deleteImage = useCallback(
    async (imageName) => {
      try {
        await deleteObject(storageRef(getStorage(), `charts/${watchId}/${imageName}`));
        setImages((prev) => prev.filter((image) => image.name !== imageName));
        if (selectedImage?.name === imageName) {
          setViewerOpen(false);
          setSelectedImage(null);
        }
        return true;
      } catch (error) {
        console.error("Error deleting image:", error);
        return false;
      }
    },
    [watchId, selectedImage]
  );

  const sweepCandidates = useMemo(() => {
    const threshold = RETENTION.find((option) => option.value === retention)?.ms ?? 0;
    const cutoff = Date.now() - threshold;
    return images.filter((image) => image.timeCreated && image.timeCreated < cutoff);
  }, [images, retention]);

  const runSweep = async () => {
    setConfirmSweep(false);
    let deleted = 0;
    for (const image of sweepCandidates) {
      // eslint-disable-next-line no-await-in-loop
      if (await deleteImage(image.name)) deleted += 1;
    }
    showSnack(
      deleted
        ? `${deleted} chart${deleted === 1 ? "" : "s"} deleted.`
        : "Nothing matched that timeframe.",
      deleted ? "success" : "info"
    );
  };

  const downloadImage = async (image) => {
    try {
      const response = await fetch(image.url, { mode: "cors" });
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = image.name || "chart.png";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Failed to save image:", error);
      window.open(image.url, "_blank", "noopener,noreferrer");
    }
  };

  const categoryLabel =
    CATEGORIES.find((option) => option.value === category)?.label ?? category;

  return (
    <div className="wg-shell">
      <SideBarForRole7 />

      <div className="wg-main">
        <NavbarForRole7 />

        <main className="wg-content">
          <div className="wg-page-head">
            <div className="wg-page-head__text">
              <p className="wg-eyebrow">Saved exports</p>
              <h1 className="wg-title">Files</h1>
              <p className="wg-subtitle">
                Charts exported from any of the {METRICS.length} metric panels are
                archived here for watch <span className="wg-mono">{watchId}</span>.
              </p>
            </div>
            <div className="wg-page-head__actions">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Metric</InputLabel>
                <Select
                  value={category}
                  label="Metric"
                  onChange={(event) => setCategory(event.target.value)}
                >
                  {CATEGORIES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          <div className="files-body">
            <section className="files-gallery">
              {loading ? (
                <div className="files-loading">
                  <CircularProgress size={28} />
                  <p className="wg-stat__meta">Loading saved charts…</p>
                </div>
              ) : images.length === 0 ? (
                <EmptyState
                  icon={<CollectionsOutlinedIcon />}
                  title={`No ${categoryLabel.toLowerCase()} charts saved`}
                  copy="Use Export on a metric panel to archive a chart snapshot here."
                />
              ) : (
                <div className="files-grid">
                  {images.map((image) => (
                    <article className="wg-card wg-card--interactive files-tile" key={image.name}>
                      <button
                        type="button"
                        className="files-tile__preview"
                        onClick={() => {
                          setSelectedImage(image);
                          setViewerOpen(true);
                        }}
                        aria-label={`Open ${image.name}`}
                      >
                        <img src={image.url} alt="" loading="lazy" />
                      </button>
                      <div className="files-tile__meta">
                        <p className="files-tile__title">{categoryLabel}</p>
                        <p className="files-tile__date">
                          {image.timeCreated
                            ? `${formatTimestamp(image.timeCreated)} · ${formatRelative(
                                image.timeCreated
                              )}`
                            : image.name}
                        </p>
                      </div>
                      <div className="files-tile__actions">
                        <Tooltip title="Download">
                          <IconButton size="small" onClick={() => downloadImage(image)}>
                            <DownloadOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => deleteImage(image.name)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <aside className="files-aside">
              <AutoDeleteImages />

              <section className="wg-card">
                <header className="wg-card__head">
                  <span className="wg-icon-badge">
                    <DeleteSweepOutlinedIcon />
                  </span>
                  <div className="wg-card__headings">
                    <h2 className="wg-card__title">Clean up now</h2>
                    <div className="wg-card__sub">Delete old {categoryLabel} charts</div>
                  </div>
                </header>
                <div className="wg-card__body wg-card__body--tight">
                  <FormControl fullWidth size="small">
                    <InputLabel>Timeframe</InputLabel>
                    <Select
                      value={retention}
                      label="Timeframe"
                      onChange={(event) => setRetention(event.target.value)}
                    >
                      {RETENTION.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <p className="wg-stat__meta" style={{ whiteSpace: "normal" }}>
                    {sweepCandidates.length} of {images.length} shown charts match.
                    Deletion is permanent.
                  </p>

                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    startIcon={<DeleteSweepOutlinedIcon />}
                    disabled={!sweepCandidates.length}
                    onClick={() => setConfirmSweep(true)}
                  >
                    Delete {sweepCandidates.length || ""} chart
                    {sweepCandidates.length === 1 ? "" : "s"}
                  </Button>
                </div>
              </section>
            </aside>
          </div>
        </main>
      </div>

      {/* --- Viewer ---------------------------------------------------- */}
      <Dialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle component="div" className="files-viewer__title">
          <span className="wg-truncate">
            {selectedImage?.timeCreated
              ? `${categoryLabel} · ${formatTimestamp(selectedImage.timeCreated)}`
              : selectedImage?.name}
          </span>
          <IconButton onClick={() => setViewerOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedImage && (
            <img
              src={selectedImage.url}
              alt={selectedImage.name}
              className="files-viewer__image"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={() => selectedImage && deleteImage(selectedImage.name)}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadOutlinedIcon />}
            onClick={() => selectedImage && downloadImage(selectedImage)}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Sweep confirmation ---------------------------------------- */}
      <Dialog open={confirmSweep} onClose={() => setConfirmSweep(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {sweepCandidates.length} charts?</DialogTitle>
        <DialogContent>
          <p className="wg-subtitle">
            This permanently removes every {categoryLabel.toLowerCase()} chart{" "}
            {RETENTION.find((option) => option.value === retention)?.label.toLowerCase()}.
            It cannot be undone.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSweep(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={runSweep}>
            Delete permanently
          </Button>
        </DialogActions>
      </Dialog>

      {snack}
    </div>
  );
};

export default FilesPage;
